import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { PageHeader, DateRange, Waiting, Tabs } from "components/ui";
import useDateRange from "hooks/useDateRange";
import {
  today,
  firstDayOfThisMonth,
  lastDayOfThisMonth,
  firstDayOfLastMonth,
  lastDayOfLastMonth
} from "utils/dates";
import moment from "moment";
import {
  fetchCallQualityDashboard,
  fetchCallQualityCalls,
  fetchCallQualityByCoach,
  fetchCallQualityByCompany,
  fetchCallQualityByTeam,
  fetchCallQualityFilterOptions,
  exportCallQuality
} from "actions/call-quality";
import { selectors } from "reducers";
import CallQualityFilters from "./call-quality-filters";
import CallQualityKpis from "./call-quality-kpis";
import ScoreDistributionChart from "./score-distribution-chart";
import SentimentChart from "./sentiment-chart";
import DispositionChart from "./disposition-chart";
import DispositionByAgentChart from "./disposition-by-agent-chart";
import HourlyDispositionChart from "./hourly-disposition-chart";
import CallsTable from "./calls-table";
import AggregationTable from "./aggregation-table";
import "./call-quality-dashboard.scss";

const CallQualityDashboard = () => {
  const dispatch = useDispatch();
  const [hasInitializedDispositions, setHasInitializedDispositions] = useState(false);

  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    dateRangeIsValid
  } = useDateRange({
    startDate: moment().subtract(30, "days").format("MM/DD/YYYY"),
    endDate: today()
  });

  const location = useLocation();
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    return {
      agentType: "",
      companyGuid: "",
      teamGuid: "",
      coachGuid: "",
      dispositions: [], // Will be populated with defaults once filter options load
      patientSearch: params.get("patientSearch") || ""
    };
  });

  const dashboard = useSelector(selectors.getCallQualityDashboard);
  const isFetchingDashboard = useSelector(selectors.getIsFetchingCallQualityDashboard);
  const calls = useSelector(selectors.getCallQualityCalls);
  const callsPage = useSelector(selectors.getCallQualityCallsPage);
  const callsPageSize = useSelector(selectors.getCallQualityCallsPageSize);
  const callsTotalCount = useSelector(selectors.getCallQualityCallsTotalCount);
  const callsTotalPages = useSelector(selectors.getCallQualityCallsTotalPages);
  const isFetchingCalls = useSelector(selectors.getIsFetchingCallQualityCalls);
  const byCoach = useSelector(selectors.getCallQualityByCoach);
  const isFetchingByCoach = useSelector(selectors.getIsFetchingCallQualityByCoach);
  const byCompany = useSelector(selectors.getCallQualityByCompany);
  const isFetchingByCompany = useSelector(selectors.getIsFetchingCallQualityByCompany);
  const byTeam = useSelector(selectors.getCallQualityByTeam);
  const isFetchingByTeam = useSelector(selectors.getIsFetchingCallQualityByTeam);
  const filterOptions = useSelector(selectors.getCallQualityFilterOptions);

  const buildParams = (page = 1, overrides = {}) => ({
    startDate,
    endDate,
    agentType: filters.agentType || null,
    companyGuid: filters.companyGuid || null,
    teamGuid: filters.teamGuid || null,
    coachGuid: filters.coachGuid || null,
    dispositions: filters.dispositions?.length > 0 ? filters.dispositions : null,
    patientSearch: filters.patientSearch || null,
    page,
    pageSize: callsPageSize,
    ...overrides
  });

  const fetchAllData = (params) => {
    dispatch(fetchCallQualityDashboard(params));
    dispatch(fetchCallQualityCalls(params));
    dispatch(fetchCallQualityByCoach(params));
    dispatch(fetchCallQualityByCompany(params));
    dispatch(fetchCallQualityByTeam(params));
  };

  const fetchData = (page = 1, overrides = {}) => {
    const params = buildParams(page, overrides);
    fetchAllData(params);
  };

  // Fetch filter options on mount
  useEffect(() => {
    dispatch(fetchCallQualityFilterOptions());
  }, [dispatch]);

  // Once filter options load, set default dispositions (all) and fetch data
  useEffect(() => {
    if (filterOptions?.dispositions?.length > 0 && !hasInitializedDispositions) {
      // Default to ALL dispositions so hourly chart shows meaningful connection rates
      const allDispositions = filterOptions.dispositions.map(d => d.value);

      setFilters(prev => ({ ...prev, dispositions: allDispositions }));
      setHasInitializedDispositions(true);
    }
  }, [filterOptions, hasInitializedDispositions]);

  // Fetch data once dispositions are initialized
  useEffect(() => {
    if (hasInitializedDispositions) {
      fetchData();
    }
  }, [hasInitializedDispositions]);

  const handleSubmit = () => {
    fetchData(1);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handlePageChange = (page) => {
    const params = buildParams(page);
    dispatch(fetchCallQualityCalls(params));
  };

  const handleExport = () => {
    dispatch(exportCallQuality(buildParams()));
  };

  const handleCoachDrillDown = (coachGuid) => {
    setFilters(prev => ({ ...prev, coachGuid }));
    fetchData(1, { coachGuid });
  };

  const handleDatePreset = (preset) => {
    let newStartDate, newEndDate;
    const todayDate = today();

    switch (preset) {
      case "last7":
        newStartDate = moment().subtract(7, "days").format("MM/DD/YYYY");
        newEndDate = todayDate;
        break;
      case "last30":
        newStartDate = moment().subtract(30, "days").format("MM/DD/YYYY");
        newEndDate = todayDate;
        break;
      case "thisMonth":
        newStartDate = firstDayOfThisMonth();
        newEndDate = lastDayOfThisMonth();
        break;
      case "lastMonth":
        newStartDate = firstDayOfLastMonth();
        newEndDate = lastDayOfLastMonth();
        break;
      default:
        return;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    fetchData(1, { startDate: newStartDate, endDate: newEndDate });
  };

  const isFetching = isFetchingDashboard || isFetchingCalls;

  const tabs = [
    {
      name: "Individual Calls",
      key: "calls",
      content: (
        <CallsTable
          calls={calls}
          isFetching={isFetchingCalls}
          page={callsPage}
          pageSize={callsPageSize}
          totalCount={callsTotalCount}
          totalPages={callsTotalPages}
          onPageChange={handlePageChange}
        />
      )
    },
    {
      name: "By Coach",
      key: "byCoach",
      content: (
        <AggregationTable
          data={byCoach}
          isFetching={isFetchingByCoach}
          nameLabel="Coach"
          onNameClick={handleCoachDrillDown}
        />
      )
    },
    {
      name: "By Company",
      key: "byCompany",
      content: (
        <AggregationTable
          data={byCompany}
          isFetching={isFetchingByCompany}
          nameLabel="Company"
        />
      )
    },
    {
      name: "By Team",
      key: "byTeam",
      content: (
        <AggregationTable
          data={byTeam}
          isFetching={isFetchingByTeam}
          nameLabel="Team"
        />
      )
    }
  ];

  return (
    <div className="call-quality-dashboard">
      <PageHeader title="Call Quality Dashboard">
        <div className="call-quality-header-controls">
          <DateRange
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            handleSubmit={handleSubmit}
            isValid={dateRangeIsValid}
            isFetching={isFetching}
          />
        </div>
      </PageHeader>

      <CallQualityFilters
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        onApply={handleSubmit}
        onDatePreset={handleDatePreset}
        onExport={handleExport}
        isFetching={isFetching}
      />

      {isFetchingDashboard ? (
        <Waiting />
      ) : (
        <>
          <CallQualityKpis dashboard={dashboard} />

          <div className="call-quality-charts">
            <div className="chart-row">
              <ScoreDistributionChart
                scoreDistribution={dashboard?.scoreDistribution}
              />
              <SentimentChart
                patientSentiment={dashboard?.patientSentiment}
              />
            </div>
            <div className="chart-row">
              <DispositionChart
                dispositionBreakdown={dashboard?.dispositionBreakdown}
              />
              <DispositionByAgentChart
                dispositionByAgentType={dashboard?.dispositionByAgentType}
              />
            </div>
            <div className="chart-row full-width">
              <HourlyDispositionChart
                hourlyDispositionData={dashboard?.hourlyDispositionData}
              />
            </div>
          </div>
        </>
      )}

      <div className="call-quality-tabs">
        <Tabs
          tabs={tabs}
          placeholder={{ s: "", css: {} }}
        />
      </div>
    </div>
  );
};

export default CallQualityDashboard;
